import { useEffect, useState, type FormEvent } from "react";

import {
  Alert,
  Box,
  Button,
  Drawer,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { createOrganization, listOrganizations, updateOrganization } from "@/api/organizations";
import { listTenants } from "@/api/tenants";
import type { Organization, OrganizationKind } from "@/api/types";
import { useAuthStore } from "@/store/authStore";

const KINDS: OrganizationKind[] = [
  "SAMU",
  "SAS",
  "CRRA",
  "SMUR",
  "CESU",
  "MMG",
  "SNP",
  "PDS",
  "SERVICE",
  "AUTRE",
];

type Props = {
  open: boolean;
  organization: Organization | null;
  onClose: () => void;
  onSaved: () => void;
  /** Pins the new organisation to one tenant, hiding the picker (GOD drill-down). */
  tenantId?: string;
};

export function OrganizationFormDrawer({
  open,
  organization,
  onClose,
  onSaved,
  tenantId: pinnedTenantId,
}: Props) {
  const { t } = useTranslation();
  const isEdit = Boolean(organization);

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [kind, setKind] = useState<OrganizationKind>("AUTRE");
  const [operatesWeekends, setOperatesWeekends] = useState(true);
  const [operatesHolidays, setOperatesHolidays] = useState(true);
  const [excludesBridgeDays, setExcludesBridgeDays] = useState(false);
  const [hoursStart, setHoursStart] = useState("00:00");
  const [hoursEnd, setHoursEnd] = useState("00:00");
  const [parentId, setParentId] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);

  // A GOD belongs to no tenant, so creating an organisation means naming one —
  // unless the caller already pinned it by drilling in from a tenant.
  const isGod = Boolean(useAuthStore((s) => s.me)?.is_god);
  const needsTenantPick = isGod && !isEdit && !pinnedTenantId;
  // Siblings only: attaching to a parent in another tenant is rejected server-side.
  const scopeTenantId = pinnedTenantId ?? organization?.tenant_id;

  const orgs = useQuery({
    queryKey: ["organizations-active", scopeTenantId ?? ""],
    queryFn: () =>
      listOrganizations({ status: "ACTIVE", limit: 200, tenant_id: scopeTenantId }),
    enabled: open,
  });

  const tenants = useQuery({
    queryKey: ["tenants", ""],
    queryFn: () => listTenants({ limit: 200 }),
    enabled: open && needsTenantPick,
  });

  // An organisation can't be its own parent, and the API rejects cycles anyway.
  const parentChoices = (orgs.data?.items ?? []).filter((o) => o.id !== organization?.id);

  useEffect(() => {
    if (open) {
      setCode(organization?.code ?? "");
      setName(organization?.name ?? "");
      setKind(organization?.kind ?? "AUTRE");
      setParentId(organization?.parent_id ?? "");
      setTenantId("");
      setOperatesWeekends(organization?.operates_weekends ?? true);
      setOperatesHolidays(organization?.operates_holidays ?? true);
      setExcludesBridgeDays(organization?.excludes_bridge_days ?? false);
      setHoursStart((organization?.operating_hours_start ?? "00:00:00").slice(0, 5));
      setHoursEnd((organization?.operating_hours_end ?? "00:00:00").slice(0, 5));
      setServerError(null);
    }
  }, [open, organization]);

  const mutation = useMutation({
    mutationFn: () =>
      isEdit && organization
        ? updateOrganization(organization.id, {
            name,
            kind,
            parent_id: parentId || null,
            operates_weekends: operatesWeekends,
            operates_holidays: operatesHolidays,
            excludes_bridge_days: excludesBridgeDays,
            operating_hours_start: `${hoursStart}:00`,
            operating_hours_end: `${hoursEnd}:00`,
          })
        : createOrganization({
            code,
            name,
            kind,
            parent_id: parentId || null,
            tenant_id: pinnedTenantId ?? (needsTenantPick ? tenantId : null),
          }),
    onSuccess: () => onSaved(),
    onError: (e: unknown) => {
      const detail =
        (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      if (detail === "duplicate_code") {
        setServerError(`Code "${code}" déjà utilisé dans le tenant`);
      } else {
        setServerError(typeof detail === "string" ? detail : (e as Error).message);
      }
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: { sx: { width: { xs: "100%", sm: 460 } } },
      }}
    >
      <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {isEdit ? t("orgs.edit") : t("orgs.create")}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Box component="form" onSubmit={onSubmit} sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Stack spacing={2}>
            {serverError && <Alert severity="error">{serverError}</Alert>}
            {needsTenantPick && (
              <FormControl fullWidth required>
                <InputLabel>{t("orgs.tenant")}</InputLabel>
                <Select
                  label={t("orgs.tenant")}
                  value={tenantId}
                  onChange={(e) => setTenantId(e.target.value)}
                >
                  {(tenants.data?.items ?? []).map((tn) => (
                    <MenuItem key={tn.id} value={tn.id}>
                      {tn.slug} — {tn.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{t("orgs.tenantHint")}</FormHelperText>
              </FormControl>
            )}
            <TextField
              label={t("orgs.code")}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              helperText={t("orgs.codeHint")}
              required
              fullWidth
              disabled={isEdit}
            />
            <TextField
              label={t("orgs.name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>{t("orgs.kind")}</InputLabel>
              <Select
                label={t("orgs.kind")}
                value={kind}
                onChange={(e) => setKind(e.target.value as OrganizationKind)}
              >
                {KINDS.map((k) => (
                  <MenuItem key={k} value={k}>
                    {t(`orgKind.${k}`, { defaultValue: k })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t("orgs.parent")}</InputLabel>
              <Select
                label={t("orgs.parent")}
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
              >
                <MenuItem value="">
                  <em>{t("orgs.parentNone")}</em>
                </MenuItem>
                {parentChoices.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.code} — {o.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{t("orgs.parentHint")}</FormHelperText>
            </FormControl>

            {isEdit && (
              <>
                <Typography
                  variant="overline"
                  sx={{ fontWeight: 700, color: "text.secondary", mt: 1 }}
                >
                  {t("orgs.operating.title")}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={operatesWeekends}
                      onChange={(e) => setOperatesWeekends(e.target.checked)}
                    />
                  }
                  label={t("orgs.operating.weekends")}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={operatesHolidays}
                      onChange={(e) => setOperatesHolidays(e.target.checked)}
                    />
                  }
                  label={t("orgs.operating.holidays")}
                />
                {/* Only meaningful once holidays are already closed. */}
                <FormControlLabel
                  disabled={operatesHolidays}
                  control={
                    <Switch
                      checked={excludesBridgeDays && !operatesHolidays}
                      onChange={(e) => setExcludesBridgeDays(e.target.checked)}
                    />
                  }
                  label={t("orgs.operating.bridgeDays")}
                />
                <Stack direction="row" spacing={1}>
                  <TextField
                    label={t("orgs.operating.start")}
                    type="time"
                    value={hoursStart}
                    onChange={(e) => setHoursStart(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label={t("orgs.operating.end")}
                    type="time"
                    value={hoursEnd}
                    onChange={(e) => setHoursEnd(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1 }}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {t("orgs.operating.helper")}
                </Typography>
              </>
            )}
          </Stack>

          <Box sx={{ flex: 1 }} />
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 2 }}>
            <Button onClick={onClose} disabled={mutation.isPending}>
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                mutation.isPending || !code || !name || (needsTenantPick && !tenantId)
              }
            >
              {t("common.save")}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
}
